import { Icon } from "@circle-react-shared/Icon";
import { Form } from "@circle-react-uikit/Form";
import type { OnSubmitFunction } from "@circle-react-uikit/Form/Form";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { OtpTitle } from "@circle-react/components/OtpChallenge/components/OtpTitle";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Button } from "@circleco/compass/components/Button";
import { Link } from "@circleco/compass/components/Link";
import { Typography } from "@circleco/compass/components/Typography";
import { useToast } from "@circleco/compass/context/CompassContext";
import { parse } from "query-string";
import { useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import type { Variant } from "@/react/contexts/OtpChallengeContext";
import { useOtpChallenge } from "@/react/hooks/otpChallenge/useOtpChallenge";

interface OtpPasswordFormState {
  password: string;
}

interface SignInResponse {
  redirect_url?: string;
}

interface OtpSignInPasswordFormProps {
  className?: string;
  email: string;
  onCancel: () => void;
  onPasswordFailed?: () => void;
  onPasswordSubmitted?: () => void;
  onPasswordSucceeded?: () => void;
  onShowOtpForm: () => void;
  shouldRedirectOnSuccess?: boolean;
  showSignInWithCode?: boolean;
  variant?: Variant;
}

export const OtpSignInPasswordForm = ({
  className,
  email,
  onShowOtpForm,
  onCancel,
  onPasswordSubmitted,
  onPasswordSucceeded,
  onPasswordFailed,
  shouldRedirectOnSuccess = true,
  showSignInWithCode = true,
  variant = "embedded",
}: OtpSignInPasswordFormProps) => {
  const { currentCommunity } = useCurrentCommunityContext();
  const toast = useToast();
  // Parse query params from window.location directly rather than via
  // useRouterQueryParams: this form also renders inside the OTP challenge
  // modal, which mounts above the app router and has no router context.
  const urlParams = useMemo(() => parse(window.location.search), []);
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: signIn } = useOtpChallenge<
    SignInResponse,
    { email: string; password: string }
  >({
    mutationFn: data =>
      reactQueryPost(authenticationAppRoutes.signIn(), { user: data }),
    email,
    variant,
  });

  // setError pushes API failures into react-hook-form so Form.Item renders both
  // the message and the standard error border, keeping required-field and
  // server-side errors on a single source of truth (matches sibling auth forms).
  const handleSubmit: OnSubmitFunction<OtpPasswordFormState> = async (
    { password },
    { setError },
  ) => {
    onPasswordSubmitted?.();
    setIsLoading(true);
    try {
      const data = await signIn({ email, password });
      if (data?.redirect_url) {
        onPasswordSucceeded?.();
        if (shouldRedirectOnSuccess) window.location.href = data.redirect_url;
      }
    } catch (err) {
      const cause = (err as { cause?: { otpChallengeCancelled?: boolean } })
        .cause;
      if (cause?.otpChallengeCancelled) return;
      const message = (err as { message: string }).message;
      onPasswordFailed?.();
      setError("password", { type: "server", message });
      toast.add({ title: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPasswordUrl = authenticationAppRoutes.forgotPassword({
    params: { ...urlParams, email },
  });

  return (
    <div
      className={classNames(
        "flex h-full flex-col items-center justify-center",
        className,
      )}
    >
      {currentCommunity && (
        <OtpTitle
          currentCommunity={currentCommunity}
          title={t("authentication_app.signup.title.welcome_community", {
            community_name: currentCommunity.name,
          })}
          subtitle={t("authentication_app.signup.subtitle_otp_first_password")}
        />
      )}
      <div className="text-primary w-full rounded-lg border border-primary p-6 sm:w-[468px]">
        <div className="bg-tertiary -mx-6 -mt-6 mb-4 flex h-14 items-center justify-between rounded-t-lg px-6">
          <Typography variant="body-sm">{email}</Typography>
          <button
            type="button"
            className="cursor-pointer text-secondary transition-colors hover:text-primary"
            onClick={onCancel}
            disabled={isLoading}
            aria-label={t("otp_challenge_modal.cancel")}
            tabIndex={-1}
          >
            <Icon type="20-close" size={20} className="size-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Use the shared Form/Form.Input pattern (as in SignInForm and
              ResetPasswordForm) so the password field gets the standard
              Compass focus/border styling. A previous hand-rolled <input>
              used a non-resolving `ring-brand` class and left the browser's
              native focus outline visible, rendering a red ring on autofocus. */}
          <Form<OtpPasswordFormState>
            defaultValues={{ password: "" }}
            onSubmit={handleSubmit}
            formClassName="flex flex-col gap-4"
          >
            <Form.Item
              isFirstChild
              id="password"
              name="password"
              label={t("otp_challenge_modal.password_state.password_label")}
              hideDescription
              hideBorder
              rules={{
                required: t(
                  "otp_challenge_modal.password_state.password_required",
                ),
              }}
            >
              <Form.Input
                type="password"
                autoFocus
                autoComplete="current-password"
                disabled={isLoading}
              />
            </Form.Item>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isLoading}
              fullWidth
            >
              {t("otp_challenge_modal.password_state.sign_in")}
            </Button>
          </Form>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-4">
              {showSignInWithCode && (
                <Link asButton size="sm" onClick={onShowOtpForm}>
                  {t("otp_challenge_modal.password_state.sign_in_with_code")}
                </Link>
              )}

              <Link size="sm" href={forgotPasswordUrl}>
                {t("otp_challenge_modal.password_state.forgot_password")}
              </Link>
            </div>

            <div className="border-primary flex w-fit items-center gap-1 rounded-md border px-2 py-1">
              <Icon type="16-lock" size={16} className="text-tertiary size-4" />
              <span className="leading-[18px] tracking-[0.2px]">
                <Typography variant="label-xs" color="tertiary">
                  {t("otp_challenge_modal.safe_and_secure")}
                </Typography>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
